import { useMemo } from "react";
import { MESSAGING_TEMPLATE_TYPE_OPTIONS } from "@constants/messaging-template-type-options";
import { SelectOption } from "@idw-types/common";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { resources } from "@constants/hardcoded-strings";
import { FormattedMessagingTemplate } from "@idw-types/data/messaging-template";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { MessagingTemplateGeneralInfoValidationErrors } from "@idw-types/common/validation/messaging-template/messaging-template-validation-errors";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { TemplateAvailabilityLevelSection } from "@containers/modals/add-messaging-template-modal/template-availability-level-section/template-availability-level-section";
import { IDWSession } from "@idw-types/data/idw-session";
import { MessagingTemplateTypes } from "@enums/messaging-template-types";

type MessagingTemplateGeneralInfoTabProps = {
    session: IDWSession;
    state: FormattedMessagingTemplate;
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    isDisabled: boolean;
    labelWidth: LabelWidthTypes;
    validationErrors: MessagingTemplateGeneralInfoValidationErrors;
    setMessagingTemplateState: (state: FormattedMessagingTemplate) => void;
};

export const MessagingTemplateGeneralInfoTab = (props: MessagingTemplateGeneralInfoTabProps) => {
    const {
        state,
        session,
        axnCompanies,
        labelWidth,
        axnRelyingParties,
        isDisabled,
        validationErrors,
        setMessagingTemplateState
    } = props;

    const selectedMessagingTemplateTypeOption: SelectOption = useMemo(() => {
        return MESSAGING_TEMPLATE_TYPE_OPTIONS.find(type => type.value === state.type);
    }, [state.type]);



    const handleMessagingTemplateNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMessagingTemplateState({
            ...state,
            name: event.target.value
        });
    }

    const handleMessagingTemplateTypeChange = (selectedOption: SelectOption<MessagingTemplateTypes>) => {
        setMessagingTemplateState({
            ...state,
            type: selectedOption.value
        });
    }

    const handleMessagingTemplateStatus = (value: any) => {
        setMessagingTemplateState({
            ...state,
            status: value ? EntityStatusTypes.ACTIVE : EntityStatusTypes.INACTIVE
        });
    }

    const resource = resources.pages.messagingTemplateDetails.tabs.generalInfo;

    return (
        <div>
            <ContentPageHeader title={resource.title} />
            <ContentPageBody isMaxWidth={true}>
                <TemplateAvailabilityLevelSection
                    isDisabled={isDisabled}
                    session={session}
                    labelWidth={labelWidth}
                    axnCompanies={axnCompanies}
                    isVericationServiceDetailsPage={false}
                    state={state}
                    serviceOrganizationId={null}
                    serviceRelyingPartyId={null}
                    setState={setMessagingTemplateState}
                    axnRelyingParties={axnRelyingParties}
                    validationErrors={validationErrors}
                />
                <InputFormGroup
                    controlId="messaging-template-name-input"
                    automationId="messaging-template-name-input"
                    label={resource.inputs.name.label}
                    ariaLabel={resource.inputs.name.label}
                    placeholder={resource.inputs.name.placeholder}
                    value={state.name}
                    onChange={handleMessagingTemplateNameChange}
                    disabled={isDisabled}
                    isRequired={true}
                    labelWidth={labelWidth}
                />
                {validationErrors.nameError}
                <SelectFormGroup
                    controlId={`messaging-template-type-select`}
                    label={resource.inputs.type.label}
                    ariaLabel={resource.inputs.type.label}
                    automationId={`messaging-template-type-select`}
                    onChange={handleMessagingTemplateTypeChange}
                    options={MESSAGING_TEMPLATE_TYPE_OPTIONS}
                    selectedOption={selectedMessagingTemplateTypeOption}
                    disabled={isDisabled}
                    isRequired={true}
                    isSearchable={false}
                    isClearable={false}
                    labelWidth={labelWidth}
                />
                <ToggleFormGroup
                    controlId={`messaging-template-status-radio-input`}
                    automationId={`messaging-template-status-radio-input`}
                    label={resource.inputs.status.label}
                    labelWidth={labelWidth}
                    onChange={handleMessagingTemplateStatus}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    checked={state.status === EntityStatusTypes.ACTIVE}
                    value={state.status}
                    isRequired={true}
                    disabled={isDisabled}
                />
            </ContentPageBody>
        </div>
    );
};